import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import { metricPatterns, dmvMetricPatterns, generateStableData } from '../data/miniChartData';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Metric descriptions for tooltips
const metricDescriptions = {
  temperature: "Air temperature affects plant growth and development",
  water: "Water availability is critical for crop health",
  soil: "Soil condition impacts nutrient availability and root development",
  nutrient: "Nutrient levels determine crop yield and quality",
  precipitation: "Rainfall patterns affect irrigation needs",
  yield: "Crop yield indicates overall farm productivity",
  humidity: "Relative humidity affects plant transpiration and disease risk",
  leafTemp: "Leaf temperature indicates plant stress levels",
  leafWetness: "Leaf wetness duration affects disease development",
  canopyCover: "Canopy cover indicates crop development stage",
  biodiversity: "Higher biodiversity improves ecosystem resilience",
  soilHealth: "Healthy soil supports sustainable agriculture",
  cropRotation: "Crop rotation breaks pest cycles and improves soil health",
  pestResistance: "Pest resistance reduces need for chemical interventions",
  carbonSequestration: "Carbon sequestration helps mitigate climate change",
  waterEfficiency: "Water efficiency reduces resource usage"
};

// Time labels for x-axis (if needed)
const timeLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MiniChart = ({ 
  color, 
  type = 'default', 
  data = null, 
  metricType = null,
  useDMVData = true, // Default to using DMV-specific data
  showTooltip = true,
  height = 40
}) => {
  // Get appropriate data based on metric type or generate random data
  const chartValues = useMemo(() => {
    if (data) return data;
    
    // Use DMV-specific patterns if available and useDMVData is true
    if (useDMVData && metricType && dmvMetricPatterns[metricType]) {
      return dmvMetricPatterns[metricType];
    }
    
    // Fall back to generic patterns if DMV-specific not available or not requested
    if (metricType && metricPatterns[metricType]) {
      return metricPatterns[metricType];
    }
    
    // Default to stable data if no specific pattern is available
    return generateStableData();
  }, [data, metricType, useDMVData]);

  // Get metric description for tooltip
  const metricDescription = metricType && metricDescriptions[metricType] 
    ? metricDescriptions[metricType] 
    : "Agricultural metric data";

  // Generate labels based on data length
  const labels = chartValues.length <= 12 
    ? timeLabels.slice(0, chartValues.length) 
    : Array.from({ length: chartValues.length }).map(() => '');

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: metricType ? metricType.charAt(0).toUpperCase() + metricType.slice(1) : 'Value',
        data: chartValues,
        borderColor: color,
        backgroundColor: `${color}20`, // 20 is hex for 12% opacity
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 3,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: showTooltip,
        callbacks: {
          title: (context) => {
            return metricType ? metricType.charAt(0).toUpperCase() + metricType.slice(1) : '';
          },
          label: (context) => {
            return `Value: ${context.parsed.y.toFixed(1)}`;
          },
          afterLabel: () => {
            return metricDescription;
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: 10,
        cornerRadius: 4,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        }
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
        min: Math.min(...chartValues) * 0.9,
        max: Math.max(...chartValues) * 1.1,
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        hoverBackgroundColor: color,
        hoverBorderColor: '#fff',
        hoverBorderWidth: 2,
      }
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
      axis: 'x'
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    },
    hover: {
      animationDuration: 200
    }
  };

  return (
    <div style={{ height: `${height}px`, width: '100%' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default MiniChart; 